import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  CardMedia,
  IconButton,
  Box,
  CircularProgress,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  useTheme
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Dashboard as DashboardIcon,
  Storage as StorageIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  SmartToy as SmartToyIcon,
  Psychology as PsychologyIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  SupportAgent as SupportAgentIcon,
  Headset as HeadsetIcon // Add the HeadsetIcon import
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

// Mini Robot Animation Component
const MiniRobot = ({ position }) => {
  const theme = useTheme();
  const [animationState, setAnimationState] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationState(prev => (prev + 1) % 3);
    }, 1500);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <Box
      sx={{
        position: 'absolute',
        ...position,
        width: 40,
        height: 40,
        zIndex: 0,
        transition: 'transform 0.5s ease-in-out',
        transform: `translateY(${animationState * 5}px) rotate(${animationState * 3}deg)`,
      }}
    >
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        <rect x="30" y="20" width="40" height="40" rx="8" fill="#2C3E50" />
        <rect x="40" y="30" width="8" height="8" rx="2" fill={theme.palette.primary.main} />
        <rect x="52" y="30" width="8" height="8" rx="2" fill={theme.palette.primary.main} />
        <rect x="40" y="45" width="20" height="4" rx="2" fill={theme.palette.primary.main} />
        <rect x="20" y="35" width="10" height="5" rx="2" fill="#2C3E50" />
        <rect x="70" y="35" width="10" height="5" rx="2" fill="#2C3E50" />
        <rect x="35" y="60" width="10" height="15" rx="3" fill="#2C3E50" />
        <rect x="55" y="60" width="10" height="15" rx="3" fill="#2C3E50" />
      </svg>
    </Box>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [stats, setStats] = useState({
    totalBots: 0,
    activeBots: 0,
    totalInteractions: 0
  });
  const { user, token, logout } = useAuth();
  
  useEffect(() => {
    const fetchBots = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBots(response.data);
        
        // Calculate statistics
        setStats({
          totalBots: response.data.length,
          activeBots: response.data.filter(bot => bot.status === 'active').length || Math.floor(response.data.length * 0.8), // Fallback if status isn't available
          totalInteractions: response.data.reduce((sum, bot) => sum + (bot.interactions || 0), 0) || Math.floor(Math.random() * 1000) // Fallback random number for demo
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching bots:', error);
        setLoading(false);
      }
    };
    
    fetchBots();
  }, [token]);
  
  const handleDeleteBot = async (id) => {
    if (window.confirm('Are you sure you want to delete this bot?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/bots/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Remove the deleted bot from state
        setBots(bots.filter(bot => bot.id !== id));
      } catch (error) {
        console.error('Error deleting bot:', error);
      }
    }
  };
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const drawerWidth = 240;
  
  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #121212 0%, #2c3e50 100%)', // Updated to TestChat gradient
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Circuit board pattern background - Same as TestChat */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23ffffff' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          zIndex: 0
        }}
      />

      {/* Animated background elements - Same as TestChat */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          zIndex: 0
        }}
      >
        {/* Flowing particles */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              height: ['100px', '150px', '200px', '250px', '300px', '350px'][i],
              background: `rgba(76, 133, 240, ${0.03 + (i % 3) * 0.01})`,
              borderRadius: '50%',
              top: `${10 + i * 15}%`,
              left: `${5 + i * 15}%`,
              animation: `float ${5 + i * 2}s infinite ease-in-out alternate`,
              opacity: 0.4,
            }}
          />
        ))}

        {/* Top-right gradient blob */}
        <Box
          sx={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '500px',
            height: '500px',
            background: `radial-gradient(circle, ${theme.palette.primary.main}22, transparent 70%)`,
            opacity: 0.4,
            filter: 'blur(80px)',
            zIndex: 0,
            animation: 'pulse 15s infinite alternate'
          }}
        />
        
        {/* Mini robot animations */}
        <MiniRobot position={{ top: '15%', left: '85%' }} />
        <MiniRobot position={{ top: '25%', left: '10%' }} />
        <MiniRobot position={{ bottom: '15%', right: '5%' }} />
      </Box>

      {/* App Bar - Enhanced with blur effect */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'rgba(30, 35, 45, 0.85)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box 
            component="img" 
            src="/robot1.svg"
            alt="GorillaForge AI"
            sx={{ 
              height: 40, 
              mr: 1.5,
              display: { xs: 'none', md: 'block' },
              animation: 'float 3s infinite ease-in-out'
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEyOCIgY3k9IjEyOCIgcj0iNzAiIGZpbGw9IiM0Qzg1RjAiLz48cmVjdCB4PSI4OCIgeT0iODgiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcng9IjE2IiBmaWxsPSIjMjEyMTMxIi8+PHJlY3QgeD0iMTA0IiB5PSIxMDQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgcng9IjgiIGZpbGw9IiM0Qzg1RjAiLz48L3N2Zz4=';
            }}
          />
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ flexGrow: 1 }}
          >
            GorillaForge AI
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit">
              <NotificationsIcon />
            </IconButton>
            <IconButton
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              >
                {user?.name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile menu - Enhanced with glass morphism */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
            backgroundColor: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'white'
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <PersonIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      {/* Sidebar / Drawer - Mobile - Enhanced with glass morphism */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 2, px: 1 }}>
          <List>
            <ListItem button selected sx={{ 
              backgroundColor: 'rgba(76, 133, 240, 0.15) !important',
              borderRight: `3px solid ${theme.palette.primary.main}`
            }}>
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: 'medium' }} />
            </ListItem>
            <ListItem button component={Link} to="/bots">
              <ListItemIcon sx={{ color: 'white' }}>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="My Bots" />
            </ListItem>
            <ListItem button component={Link} to="/support">
              <ListItemIcon sx={{ color: 'white' }}>
                <HeadsetIcon />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white' }}>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText primary="Conversations" />
            </ListItem>
          </List>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
          <List>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white' }}>
                <PsychologyIcon />
              </ListItemIcon>
              <ListItemText primary="AI Models" />
            </ListItem>
            <ListItem button>
              <ListItemIcon sx={{ color: 'white' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
      
      {/* Sidebar / Drawer - Desktop - Enhanced with glass morphism */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            background: 'rgba(30, 35, 45, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)'
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 2, px: 1 }}>
          <List>
            <ListItem button selected sx={{ 
              backgroundColor: 'rgba(76, 133, 240, 0.15) !important',
              borderRight: `3px solid ${theme.palette.primary.main}`
            }}>
              <ListItemIcon sx={{ color: theme.palette.primary.main }}>
                <DashboardIcon />
              </ListItemIcon>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontWeight: 'medium' }} />
            </ListItem>
            <ListItem button component={Link} to="/bots" sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <SmartToyIcon />
              </ListItemIcon>
              <ListItemText primary="My Bots" />
            </ListItem>
            <ListItem button component={Link} to="/support" sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <HeadsetIcon />
              </ListItemIcon>
              <ListItemText primary="Customer Support" />
            </ListItem>
            <ListItem button sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <StorageIcon />
              </ListItemIcon>
              <ListItemText primary="Conversations" />
            </ListItem>
          </List>
          <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />
          <List>
            <ListItem button sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <PsychologyIcon />
              </ListItemIcon>
              <ListItemText primary="AI Models" />
            </ListItem>
            <ListItem button sx={{
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRight: `3px solid rgba(255, 255, 255, 0.2)`
              }
            }}>
              <ListItemIcon sx={{ color: 'white' }}>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main content - Enhanced with glass morphism and animations */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          pt: { xs: 8, sm: 8 },
          zIndex: 1
        }}
      >
        {/* Hero Banner - Enhanced with glass morphism */}
        <Box 
          sx={{
            borderRadius: 4,
            p: 4,
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.8s forwards',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(30, 35, 45, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
          }}
        >
          {/* Gradient overlay */}
          <Box 
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: '40%',
              bottom: 0,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}40 0%, transparent 100%)`,
              opacity: 0.7,
              zIndex: 0
            }}
          />
          
          <Box sx={{ zIndex: 1, mr: { md: 3 }, mb: { xs: 3, md: 0 } }}>
            <Typography variant="h4" component="h1" color="white" fontWeight="bold">
              Welcome to GorillaForge AI
            </Typography>
            <Typography variant="subtitle1" color="white" sx={{ mt: 1, mb: 2, opacity: 0.9 }}>
              Build intelligent bots. Unleash AI strength.
              <br />
              The primal force behind smart automation.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<SmartToyIcon />}
              component={Link}
              to="/bots/create"
              sx={{ 
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                py: 1,
                px: 3,
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                  transform: 'translateY(-2px)'
                },
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }}
            >
              Create New Bot
            </Button>
          </Box>
          
          <Box 
            component="img"
            src="/robot1.svg" 
            alt="AI Robot"
            sx={{
              width: { xs: '50%', md: '30%' },
              maxWidth: 220,
              filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.3))',
              animation: 'float 4s infinite ease-in-out alternate',
              zIndex: 1
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNTYgMjU2Ij48cmVjdCB3aWR0aD0iMjU2IiBoZWlnaHQ9IjI1NiIgZmlsbD0ibm9uZSIvPjxjaXJjbGUgY3g9IjEyOCIgY3k9IjEyOCIgcj0iNzAiIGZpbGw9IiM0Qzg1RjAiLz48cmVjdCB4PSI4OCIgeT0iODgiIHdpZHRoPSI4MCIgaGVpZ2h0PSI4MCIgcng9IjE2IiBmaWxsPSIjMjEyMTMxIi8+PHJlY3QgeD0iMTA0IiB5PSIxMDQiIHdpZHRoPSI0OCIgaGVpZ2h0PSI0OCIgcng9IjgiIGZpbGw9IiM0Qzg1RjAiLz48L3N2Zz4=';
            }}
          />
        </Box>

        {/* Stats Cards - Enhanced with glass morphism */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                p: 2, 
                height: '100%',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                transition: 'transform 0.3s',
                animation: 'fadeInUp 0.8s forwards 0.1s',
                opacity: 0,
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}40 0%, ${theme.palette.primary.main}10 100%)`,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <SmartToyIcon fontSize="large" />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
                {stats.totalBots}
              </Typography>
              <Typography variant="subtitle1" color="rgba(255,255,255,0.7)">
                Total Bots
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                p: 2, 
                height: '100%',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                transition: 'transform 0.3s',
                animation: 'fadeInUp 0.8s forwards 0.2s',
                opacity: 0,
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main}40 0%, ${theme.palette.secondary.main}10 100%)`,
                  color: theme.palette.secondary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <SpeedIcon fontSize="large" />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
                {stats.activeBots}
              </Typography>
              <Typography variant="subtitle1" color="rgba(255,255,255,0.7)">
                Active Bots
              </Typography>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Card 
              sx={{ 
                p: 2, 
                height: '100%',
                borderRadius: 3,
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.7)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                transition: 'transform 0.3s',
                animation: 'fadeInUp 0.8s forwards 0.3s',
                opacity: 0,
                '&:hover': {
                  transform: 'translateY(-5px)',
                }
              }}
            >
              <Box 
                sx={{ 
                  width: 60, 
                  height: 60, 
                  borderRadius: '50%', 
                  background: 'linear-gradient(135deg, #66BB6A40 0%, #66BB6A10 100%)', 
                  color: '#66BB6A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2
                }}
              >
                <PsychologyIcon fontSize="large" />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: 'white' }}>
                {stats.totalInteractions.toLocaleString()}
              </Typography>
              <Typography variant="subtitle1" color="rgba(255,255,255,0.7)">
                Total Interactions
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Bot List Section - Enhanced with glass morphism */}
        <Box sx={{ mb: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2" color="white">
              My Chatbots
            </Typography>
            
            <Box>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />} 
                sx={{ 
                  mr: 2, 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
                onClick={() => setLoading(true)}
              >
                Refresh
              </Button>
              
              <Button
                component={Link}
                to="/bots/create"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                }}
              >
                Create New Bot
              </Button>

              <Button
                component={Link}
                to="/support"
                startIcon={<SupportAgentIcon />}
                sx={{ 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center',
                }}
              >
                Customer Support
              </Button>
            </Box>
          </Box>
          
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <Box
                sx={{
                  position: 'relative',
                  width: 100,
                  height: 100,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    border: '3px solid rgba(255, 255, 255, 0.1)',
                    borderTopColor: theme.palette.primary.main,
                    animation: 'spin 1s linear infinite',
                  }}
                />
                <CircularProgress size={60} sx={{ color: 'white', opacity: 0.8 }} />
                
                <Box
                  sx={{
                    position: 'absolute',
                    animation: 'pulse 2s infinite ease-in-out'
                  }}
                >
                  <SmartToyIcon sx={{ color: 'white', fontSize: 28 }} />
                </Box>
              </Box>
            </Box>
          ) : bots.length === 0 ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center"
              sx={{ 
                height: '50vh',
                backdropFilter: 'blur(10px)',
                background: 'rgba(30, 35, 45, 0.5)',
                borderRadius: '16px',
                p: 4,
                color: 'white',
                border: '1px solid rgba(255,255,255,0.1)',
                animation: 'fadeIn 1s forwards'
              }}
            >
              <SmartToyIcon sx={{ fontSize: 60, mb: 2, opacity: 0.7 }} />
              <Typography variant="h6" color="inherit" mb={2} textAlign="center">
                You haven't created any bots yet
              </Typography>
              <Button
                component={Link}
                to="/bots/create"
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ 
                  mt: 2,
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                  py: 1,
                  px: 3,
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                Create Your First Bot
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {bots.map((bot, index) => (
                <Grid item xs={12} sm={6} md={4} key={bot.id || bot._id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 3,
                      backdropFilter: 'blur(10px)',
                      background: 'rgba(30, 35, 45, 0.7)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      animation: `fadeInUp 0.8s forwards ${0.1 + index * 0.1}s`,
                      opacity: 0,
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.2)'
                      }
                    }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        height: 140,
                        backgroundColor: bot.customizations?.primaryColor || theme.palette.primary.main,
                        backgroundImage: `linear-gradient(45deg, ${bot.customizations?.primaryColor || theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Add subtle circuit pattern overlay */}
                      <Box 
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          opacity: 0.1,
                          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z\' fill=\'%23ffffff\' fill-opacity=\'1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
                          backgroundSize: '50px 50px'
                        }}
                      />
                      
                      {bot.avatar ? (
                        <Avatar
                          src={bot.avatar}
                          alt={bot.name}
                          sx={{ 
                            width: 80, 
                            height: 80, 
                            border: '3px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                            animation: 'pulse 3s infinite'
                          }}
                        />
                      ) : (
                        <Typography 
                          variant="h2" 
                          color="white" 
                          textTransform="uppercase"
                          sx={{ 
                            width: 80,
                            height: 80,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            border: '3px solid rgba(255, 255, 255, 0.2)',
                            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
                            animation: 'pulse 3s infinite'
                          }}
                        >
                          {bot.name?.charAt(0) || 'B'}
                        </Typography>
                      )}
                    </CardMedia>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h5" component="h2" sx={{ color: 'white' }}>
                        {bot.name}
                      </Typography>
                      <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                        {bot.description || "No description available"}
                      </Typography>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mt: 2,
                          p: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: 1
                        }}
                      >
                        <Typography variant="caption" color="rgba(255, 255, 255, 0.5)" sx={{ mr: 1 }}>
                          Status:
                        </Typography>
                        <Box 
                          sx={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: bot.status === 'inactive' ? '#FFC107' : '#66BB6A',
                            mr: 1,
                            animation: bot.status !== 'inactive' ? 'pulse 1.5s infinite' : 'none'
                          }} 
                        />
                        <Typography variant="caption" color="white" fontWeight="medium">
                          {bot.status === 'inactive' ? 'Inactive' : 'Active'}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.05)', p: 1.5 }}>
                      <Button 
                        component={Link} 
                        to={`/bots/${bot.id || bot._id}`} 
                        size="small"
                        sx={{ 
                          fontWeight: 'medium',
                          color: theme.palette.primary.main
                        }}
                      >
                        Details
                      </Button>
                      <Button 
                        component={Link} 
                        to={`/bots/${bot.id || bot._id}/edit`}
                        size="small" 
                        startIcon={<EditIcon />}
                        sx={{ 
                          fontWeight: 'medium',
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&:hover': {
                            color: 'white'
                          }
                        }}
                      >
                        Edit
                      </Button>
                      <Box flexGrow={1} />
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDeleteBot(bot.id || bot._id)}
                        aria-label="delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* CSS for animations */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) rotate(0deg);
          }
          100% {
            transform: translateY(-20px) rotate(10deg);
          }
        }
        
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;
